import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '../lib/cn.js'

export interface DialogProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** يمنع الإغلاق بالنقر خارج النافذة أو بمفتاح Esc — للنماذج التي تحتوي تعديلات */
  dismissible?: boolean
}

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const

/** نافذة حوارية مبنية على <dialog> الأصلي — حصر التركيز وEsc مجاناً */
export function Dialog({
  open, onClose, title, description, children, footer, size = 'md', dismissible = true,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    else if (!open && el.open) el.close()
  }, [open])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onCancel = (e: Event) => {
      e.preventDefault() // نتحكّم بالإغلاق بأنفسنا حتى تعمل dismissible
      if (dismissible) onClose()
    }
    el.addEventListener('cancel', onCancel)
    return () => el.removeEventListener('cancel', onCancel)
  }, [dismissible, onClose])

  if (!open) return null

  return (
    <dialog
      ref={ref}
      dir="rtl"
      className={cn(
        'm-auto w-[calc(100vw-2rem)] rounded-2xl border border-border bg-surface p-0 text-fg shadow-xl',
        'backdrop:bg-[var(--overlay)] backdrop:backdrop-blur-[2px]',
        'animate-[scale-in_0.16s_var(--ease-out-quint)] open:flex open:flex-col max-h-[calc(100dvh-4rem)]',
        sizes[size],
      )}
      onClick={(e) => {
        // النقر على الخلفية فقط — لا على محتوى النافذة
        if (dismissible && e.target === ref.current) onClose()
      }}
    >
      {(title || dismissible) && (
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            {title && <h2 className="text-base font-semibold text-fg">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-fg-muted">{description}</p>}
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              className="-me-1 rounded-lg p-1.5 text-fg-subtle transition-colors hover:bg-surface-hover hover:text-fg"
              aria-label="إغلاق"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

      {footer && (
        <div className="flex items-center justify-end gap-2 border-t border-border bg-bg-subtle px-5 py-3">
          {footer}
        </div>
      )}
    </dialog>
  )
}
